import { OWNER_ONLY } from "../context/MembershipContext";

/**
 * The help's topics, in reading order — the order the list shows them and the
 * "next topic" button walks through them.
 *
 * Each topic is offered on the same terms as the screen it explains, so a
 * cashier is not handed a chapter on screens they cannot open:
 * - `permission` is what navigation.js asks for (a list means any of them);
 * - `module` is the plan module the shop may have switched off;
 * - `routes` are the screens a topic describes, so the help button can offer
 *   "about this page" on the one you are looking at.
 * A topic with neither permission nor module is for everyone.
 *
 * What a topic says lives in content/<key>.js, in all three languages.
 */
export const SECTIONS = [
  { key: "overview", group: "start", icon: "👋" },
  { key: "whatsnew", group: "start", icon: "✨" },
  { key: "dashboard", group: "start", icon: "📊", permission: "analytics.view", routes: ["/admin"] },
  { key: "shop", group: "start", icon: "🏪", permission: "shop.view", routes: ["/admin/shop"] },
  { key: "multishop", group: "start", icon: "🏬", flag: "multi_shop" },

  { key: "products", group: "catalogue", icon: "📦", permission: "products.view", routes: ["/admin/products"] },
  { key: "ranges", group: "catalogue", icon: "🗂️", permission: "products.view", routes: ["/admin/ranges"] },
  { key: "packs", group: "catalogue", icon: "🧰", permission: "packs.view", module: "packs", routes: ["/admin/packs"] },
  { key: "offers", group: "catalogue", icon: "🎁", permission: "offers.view", module: "offers", routes: ["/admin/offers"] },

  { key: "register", group: "sales", icon: "🧾" },
  { key: "doses", group: "sales", icon: "💧", flag: "dose_sales" },
  { key: "sales", group: "sales", icon: "💰", permission: "purchases.view", routes: ["/admin/purchases"] },
  { key: "preorders", group: "sales", icon: "📅", permission: "preorders.view", module: "preorders", routes: ["/admin/preorders"] },
  { key: "credit", group: "sales", icon: "🤝", permission: "credit.view", module: "credit_sales", routes: ["/admin/credit"] },

  { key: "cash", group: "money", icon: "💵", module: "cash_drawer", routes: ["/admin/cash"] },
  { key: "savings", group: "money", icon: "🏦", permission: OWNER_ONLY, module: "savings", routes: ["/admin/savings"] },
  { key: "revenue", group: "money", icon: "🧮", permission: "revenue.view", module: "finance", routes: ["/admin/revenue"] },
  { key: "expenses", group: "money", icon: "💸", permission: "expenses.view", module: "finance", routes: ["/admin/expenses"] },

  { key: "stock", group: "stock", icon: "📉", permission: "products.view", routes: ["/admin/stock"] },
  {
    key: "supply",
    group: "stock",
    icon: "🚚",
    permission: ["deliveries.view", "suppliers.view"],
    module: "finance",
    routes: ["/admin/deliveries", "/admin/suppliers"],
  },
  {
    key: "mixing",
    group: "stock",
    icon: "🧪",
    permission: "mixing.view",
    module: "mixing",
    routes: ["/admin/materials", "/admin/recipes", "/admin/batches"],
  },

  { key: "reminders", group: "team", icon: "⏰", permission: OWNER_ONLY, module: "reminders", routes: ["/admin/reminders"] },
  { key: "staff", group: "team", icon: "👥", permission: OWNER_ONLY, routes: ["/admin/staff"] },
  { key: "devices", group: "team", icon: "📱", permission: OWNER_ONLY, routes: ["/admin/devices"] },
  { key: "modules", group: "team", icon: "🧩", permission: OWNER_ONLY, flag: "modules_screen", routes: ["/admin/modules"] },
  { key: "subscription", group: "team", icon: "💳", permission: OWNER_ONLY, flag: "owner_subscription", routes: ["/admin/subscription"] },
  { key: "referrals", group: "team", icon: "🎟️", permission: OWNER_ONLY, flag: "referrals", routes: ["/admin/referrals"] },
  { key: "journal", group: "team", icon: "📜", permission: OWNER_ONLY, module: "journal", routes: ["/admin/journal"] },
  { key: "settings", group: "team", icon: "⚙️", permission: "settings.view", routes: ["/admin/settings"] },

  { key: "glossary", group: "reference", icon: "📖" },
  { key: "faq", group: "reference", icon: "💡" },
];

/** Headings of the list, in the order they appear. */
export const SECTION_GROUPS = ["start", "catalogue", "sales", "money", "stock", "team", "reference"];

/**
 * The release the "What's new" topic describes. Change it when the topic is
 * rewritten for a newer update: the help button shows a dot for everyone who
 * has not opened the topic since.
 */
export const WHATS_NEW_ID = "2026-10-m2";

/** Whether a topic is offered, given the member's `can` and the shop's `moduleOn`. */
export const isSectionOffered = (section, { can, moduleOn, flag }) => {
  const needed = [].concat(section.permission || []);
  const allowed = needed.length === 0 || needed.some((permission) => can(permission));
  // A topic about a feature not rolled out to this shop would describe what it cannot see
  return allowed && (!section.module || moduleOn(section.module)) && (!section.flag || (flag ? flag(section.flag) : false));
};

/**
 * The topic about the screen at this address, if there is one. A topic claiming
 * a longer address wins, so /admin only matches the dashboard itself.
 */
export const sectionForPath = (pathname, sections) => {
  const path = pathname.replace(/\/+$/, "") || "/";
  let best = null;
  let bestLength = -1;

  sections.forEach((section) => {
    (section.routes || []).forEach((route) => {
      const matches = route === "/admin" ? path === route : path === route || path.startsWith(`${route}/`);
      if (matches && route.length > bestLength) {
        best = section;
        bestLength = route.length;
      }
    });
  });

  return best;
};
